import React, { useEffect, useRef, useState } from "react";

const PDFViewer = ({ _url }) => {
  const viewerRef = useRef(null);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const loadPDF = async () => {
      try {
        if (!isMounted) return;
        setIsLoading(true);

        if (!window.pdfjsLib && !scriptLoadedRef.current) {
          scriptLoadedRef.current = true;

          const script = document.createElement("script");
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.10.377/pdf.min.js";
          script.async = true;

          const loadScript = new Promise((resolve) => {
            script.onload = resolve;
          });

          document.body.appendChild(script);
          await loadScript;

          window.pdfjsLib = window["pdfjs-dist/build/pdf"];
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.10.377/pdf.worker.min.js";
        }

        const pdfjsLib = window.pdfjsLib || window["pdfjs-dist/build/pdf"];

        if (!isMounted) return;

        const pdfDoc = await pdfjsLib.getDocument(_url).promise;

        if (viewerRef.current) {
          viewerRef.current.innerHTML = "";
        }

        const renderPage = async (pageNum) => {
          if (!isMounted) return;

          const page = await pdfDoc.getPage(pageNum);
          const canvas = document.createElement("canvas");
          viewerRef.current?.appendChild(canvas);

          const pixelRatio = window.devicePixelRatio || 2; // Use device pixel ratio or default to 2
          const containerWidth =
            containerRef.current?.offsetWidth || window.innerWidth;

          const viewport = page.getViewport({ scale: 1 });
          const scale = containerWidth / viewport.width;
          const scaledViewport = page.getViewport({
            scale: scale * pixelRatio,
          });

          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;

          // Set the display size of the canvas
          canvas.style.width = `${containerWidth - 30}px`;
          canvas.style.height = `${scaledViewport.height / pixelRatio}px`;
          canvas.style.display = "block";
          canvas.style.padding = "10px";
          canvas.style.margin = "auto";
          canvas.style.boxShadow = "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px";

          canvas.style.marginBottom = pageNum < pdfDoc.numPages ? "8px" : "0";

          const context = canvas.getContext("2d", {
            alpha: false,
            willReadFrequently: true,
          });

          if (context) {
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = "high";

            await page.render({
              canvasContext: context,
              viewport: scaledViewport,
            }).promise;
          }
        };

        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
          await renderPage(pageNum);
        }

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error loading PDF:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isMounted = false;
    };
  }, [_url]);

  return (
    <div
      ref={containerRef}
      className="w-full"
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      {isLoading && (
        <div className="w-full text-center py-4">Loading PDF...</div>
      )}
      <div
        ref={viewerRef}
        className="w-full"
        style={{
          width: "100%",
          WebkitOverflowScrolling: "touch",
          visibility: isLoading ? "hidden" : "visible",
        }}
      />
    </div>
  );
};

export default PDFViewer;
